import * as React from "react";
import { useEffect, useState } from "react";
import { isEqual } from "lodash";

import { SmallStickerCard } from "@/components/Cells/SmallSticker";
import { UploadMultipleElements } from "@/components/UploadMultipleElements";
import { PlayersServices } from "@/services";
import { mediaElementSticket } from "@/utils";
import * as DTO from "../../interfaces";

import { getDownloadURL } from "firebase/storage";
import { Alert } from "@/components/ui";
import { AlertCircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface OwnProps {
  withWritePermission: boolean;
  playerId: number | string;
  onChange: (stickerRequest: DTO.PlayerStickerRequest) => void;
  onLoading: (loading: boolean) => void;
}

type Props = OwnProps;

export const PlayerStickers: React.FC<Props> = ({ withWritePermission = false, playerId, onChange, onLoading }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [stickers, setStickers] = useState<DTO.MediaGroup | undefined>(undefined);
  const [stickersDic, setStickersDic] = useState<{ [key: string]: DTO.StickerPreview } | undefined>(undefined);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [initialAutostickerId, setInitialAutostickerId] = useState<number>();
  const [autostickerId, setAutostickerId] = useState<number>();

  useEffect(() => {
    setLoading(true);
    const getStickers = async () => {
      const result = await PlayersServices.getPlayerStickers(playerId);

      if (result.autostickerIndex !== undefined) {
        const autoSticker = result.stickers?.media?.find(m => m.sortOrder === result.autostickerIndex);
        setInitialAutostickerId(autoSticker?.id);
        setAutostickerId(autoSticker?.id);
      }

      setStickers(result.stickers);
      if (result.stickers) {
        setStickersDic(() => {
          let dicStickers = {};
          result.stickers!!.media!!.forEach((media, id) => {
            dicStickers = {
              ...dicStickers,
              [id]: {
                uid: id,
                loading: false,
                media,
              },
            };
          });

          return {
            ...dicStickers,
          };
        });
      }

      setLoading(false);
    };

    getStickers().catch(console.error);
  }, [playerId]);

  useEffect(() => {
    if (stickersDic) {
      const imageLoading = Object.values(stickersDic).reduce(
        (accumulator, stickerValue) => stickerValue.loading || accumulator,
        false
      );

      const initialMediaUrls = stickers?.media.map(e => e.uri) || [];
      const finalMediaUrls = Object.values(stickersDic).map(m => m.media?.uri || "") || [];
      if (
        !imageLoading &&
        (!isEqual(initialMediaUrls.sort(), finalMediaUrls.sort()) || autostickerId !== initialAutostickerId)
      ) {
        prepareRequest();
      }

      setLoading(imageLoading);
    }
  }, [stickersDic, autostickerId]);

  useEffect(() => {
    onLoading(loading);
  }, [loading]);

  const prepareRequest = () => {
    if (stickersDic) {
      if (Object.values(stickersDic!).length > 100) {
        setGeneralErrors(() => ["Only 100 stickers are allowed"]);
        return;
      }

      const newMedia = Object.values(stickersDic).map((dicElm: DTO.StickerPreview, index) => {
        return {
          ...dicElm!!.media,
          sortOrder: index,
        } as DTO.Media;
      });

      const autostickerIndex = newMedia.find(m => m.id === autostickerId)?.sortOrder;

      const request: DTO.PlayerStickerRequest = {
        playerId,
        stickers: {
          ...stickers,
          media: newMedia,
          type: "MIX",
        },
        autostickerIndex,
      };
      onChange(request);
    }
  };

  const stickerToFile = (file: DTO.SocialFiles): DTO.StickerPreview => {
    file.uploadTask
      ?.then(() => {
        getDownloadURL(file.uploadTask!.snapshot.ref)
          .then(url => {
            setStickersDic(dicStc => {
              if (dicStc && dicStc[file.uid]) {
                return {
                  ...dicStc,
                  [file.uid]: {
                    ...dicStc[file.uid],
                    loadStatus: "SUBMITTED",
                    loading: false,
                    media: mediaElementSticket(url, "png", "IMAGE", Object(dicStc.keys).length),
                  },
                };
              }
              return {
                ...dicStc,
              };
            });
          })
          .catch(_ => {
            setStickersDic(dicStc => {
              if (dicStc && dicStc[file.uid]) {
                return {
                  ...dicStc,
                  [file.uid]: {
                    ...dicStc[file.uid],
                    loading: false,
                    loadStatus: "FAILED",
                  },
                };
              }
              return {
                ...dicStc,
              };
            });
          });
      })
      .catch(_ => {
        setStickersDic(dicStc => {
          if (dicStc && dicStc[file.uid]) {
            return {
              ...dicStc,
              [file.uid]: {
                ...dicStc[file.uid],
                loadStatus: "FAILED",
              },
            };
          }
          return {
            ...dicStc,
          };
        });
      });

    return {
      loading: true,
      uid: file.uid,
      uploadTask: file.uploadTask,
      thumbnail: file.thumbnail,
    };
  };

  const deleteElement = (key: string): void => {
    setStickersDic(surls => {
      if (surls) {
        delete surls[key];
      }

      return {
        ...surls,
      };
    });
  };

  return (
    <div
      className={cn("flex flex-col gap-4 w-full bg-white", !withWritePermission && "opacity-50 pointer-events-none")}
    >
      <div className="flex flex-col gap-4 w-full">
        {generalErrors.length > 0 && (
          <div className="flex flex-col gap-2">
            {generalErrors.map(error => (
              <Alert key={error} variant="destructive">
                <AlertCircleIcon className="size-4" />
                <Alert.Title>Error</Alert.Title>
                <Alert.Description>{error}</Alert.Description>
              </Alert>
            ))}
          </div>
        )}
        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 flex flex-col items-center justify-center">
          <div className="w-full">
            <UploadMultipleElements
              accept={"image/png, image/jpeg"}
              fileName={`customPlayerStickers/${playerId}`}
              nodeName={"sticker_"}
              title={"Browse Files"}
              secondary={true}
              customMessage={"Drag and drop image files here"}
              onChangeImage={file => {
                setStickersDic(cstickers => {
                  return {
                    ...cstickers,
                    [file.uid]: stickerToFile(file),
                  };
                });
              }}
            />
          </div>
        </div>

        {stickersDic && Object.keys(stickersDic).length > 0 && (
          <div className="flex flex-wrap gap-2 bg-white w-full pb-4">
            {Object.keys(stickersDic)?.map((key: string) => {
              const dicElemp = stickersDic[key];
              const uri = dicElemp.media ? dicElemp.media.uri : dicElemp.thumbnail;
              return (
                <SmallStickerCard
                  key={key}
                  uri={uri!!}
                  isAutosticker={autostickerId !== undefined && dicElemp.media?.id === autostickerId}
                  onDelete={() => deleteElement(key)}
                  onSetAutosticker={() => {
                    setAutostickerId(dicElemp.media?.id);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
